import { PALETTE } from './palette'
import './exceljsShim'
import type ExcelJS from 'exceljs'

/**
 * M10-4：monday.com 的 XLSX 导出 → 我们的导入形状
 *   { version: 1, board: {name}, columns: [...], groups: [...], items: [...] }
 *
 * 样例结构（两种都在 F:\DSH\monday_export_examples 里）：
 *   行1：看板名（单格）
 *   行2：可能是描述（Projects）或分组名（Server Ports）
 *   之后：**单格非空行 = 分组名**（颜色在**字体颜色**上，不是填充色）；其下一行 = 表头；
 *         再往下直到下一个分组行都是条目；**整行全空才跳过**（不因空行停止）。
 *
 * 颜色：
 *   · Status 类列 → 单元格**填充色**决定标签与颜色映射（类型判为 color）
 *   · 分组名 → 单元格**字体颜色** → 映射到我们既有的分组色名
 *
 * 其余按特征推断：整列数字 → numeric；整列日期 → date；逗号分隔多值 → tag；否则 text。
 */

export interface MondayParseResult {
  payload: {
    version: 1
    board: { name: string }
    columns: {
      key: string
      title: string
      type: string
      width: number
      labels?: string
      settings?: string
    }[]
    groups: { title: string; color: string }[]
    items: { name: string; groupTitle: string; values: Record<string, string> }[]
  }
  /** 来源：friday = 我们自己导出的（带 friday-meta），monday = monday.com 导出 */
  source: 'friday' | 'monday'
  /** 看起来是 monday「进度」列的候选（百分比特征），导入前可让用户选择是否按进度导入 */
  progressCandidates: { key: string; title: string; sample: string }[]
  warnings: string[]
}

/** 我们库里分组色的取值（与 UI 调色板一致） */
// 分组色直接取自 palette（唯一来源），避免两份色名表漂移导致「颜色丢失」
const GROUP_COLORS: { hex: string; name: string }[] = Object.entries(PALETTE).map(([name, hex]) => ({
  name,
  hex: hex.replace('#', '').toLowerCase(),
}))

function argb(v: unknown): string {
  if (v === null || v === undefined) return ''
  const s = String(v).replace(/^#/, '').toUpperCase()
  return s.length === 8 ? s.slice(2) : s
}

/** 最近色（按 RGB 欧氏距离）→ 我们的颜色名 */
function nearestGroupColor(hex: string): string {
  const h = argb(hex)
  if (!/^[0-9A-F]{6}$/.test(h)) return 'blue'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  let best = GROUP_COLORS[0] as { hex: string; name: string }
  let bestD = Number.MAX_VALUE
  for (const c of GROUP_COLORS) {
    const cr = parseInt(c.hex.slice(0, 2), 16)
    const cg = parseInt(c.hex.slice(2, 4), 16)
    const cb = parseInt(c.hex.slice(4, 6), 16)
    const d = (r - cr) ** 2 + (g - cg) ** 2 + (b - cb) ** 2
    if (d < bestD) {
      bestD = d
      best = c
    }
  }
  return best.name
}

function cellText(cell: ExcelJS.Cell): string {
  const v = cell.value
  if (v === null || v === undefined) return ''
  if (typeof v === 'object' && v !== null && 'richText' in v) {
    return (v as ExcelJS.CellRichTextValue).richText.map((x) => x.text).join('')
  }
  if (v instanceof Date) return v.toISOString().slice(0, 10)
  if (typeof v === 'object' && v !== null && 'result' in v) return String((v as { result?: unknown }).result ?? '')
  return String(v).trim()
}

function cellFill(cell: ExcelJS.Cell): string {
  const f = cell.fill as ExcelJS.Fill | undefined
  if (f === undefined || f === null || f.type !== 'pattern') return ''
  const fg = (f as ExcelJS.FillPattern).fgColor
  return fg === undefined ? '' : argb((fg as { argb?: string }).argb)
}

function cellFont(cell: ExcelJS.Cell): string {
  const c = cell.font?.color as { argb?: string } | undefined
  return c?.argb === undefined ? '' : argb(c.argb)
}
/** backlog #7：我们自己导出的 xlsx 会在颜色单元格上留批注 friday:idx=<索引> */
function cellNoteIndex(cell: ExcelJS.Cell): string | null {
  const n = (cell as unknown as { note?: unknown }).note
  let text = ''
  if (typeof n === 'string') text = n
  else if (n !== null && typeof n === 'object') {
    const o = n as { texts?: { text?: string }[] }
    if (Array.isArray(o.texts)) text = o.texts.map((t) => t.text ?? '').join('')
  }
  const m = /friday:idx=(\d+)/.exec(text)
  return m === null ? null : (m[1] ?? null)
}


/** 白/表头灰不算有效色 */
function meaningful(hex: string): boolean {
  const h = argb(hex)
  if (h === '') return false
  if (h === 'FFFFFF' || h === 'D6D6D6') return false
  return true
}

const isDateLike = (s: string): boolean => /^\d{4}-\d{2}-\d{2}/.test(s) || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)
const isNumberLike = (s: string): boolean => /^-?\d+(\.\d+)?$/.test(s.replace(/,/g, ''))
const isTagLike = (s: string): boolean => s.includes(',') && s.split(',').map((x) => x.trim()).filter((x) => x !== '').length >= 2

export async function parseMondayXlsx(
  file: File,
  opts: { sheetToSkip?: string } = {},
): Promise<MondayParseResult> {
  // 按需加载：exceljs 体积大（约 1MB），只在真正导入/导出 XLSX 时才拉进来
  const ExcelJS = (await import('exceljs')).default
  const wb = new ExcelJS.Workbook()
  const buf = await file.arrayBuffer()
  await wb.xlsx.load(buf)
  const warnings: string[] = []
  const ws =
    wb.worksheets.find((w) => w.name.toLowerCase() !== (opts.sheetToSkip ?? 'updates')) ??
    wb.worksheets[0]
  if (ws === undefined) throw new Error('这个 xlsx 里没有工作表')

  // 我们自己导出的 xlsx 会在隐藏表 friday-meta 里存列定义（类型/宽度/标签/设置），用来区分来源并精确还原
  let meta: { columns?: { key: string; title: string; type: string; width?: number; labels?: string; settings?: string; isName?: boolean }[] } | null = null
  const metaSheet = wb.getWorksheet('friday-meta')
  if (metaSheet !== undefined) {
    try {
      const cell = metaSheet.getCell(1, 1).value
      const text = typeof cell === 'string' ? cell : String(cell ?? '')
      meta = JSON.parse(text) as { columns?: { key: string; title: string; type: string; width?: number; labels?: string; settings?: string; isName?: boolean }[] } | null
    } catch {
      meta = null
    }
  }

  const rowCount = ws.rowCount
  const colCount = ws.columnCount
  const rows: { r: number; texts: string[]; fills: string[]; fonts: string[]; notes: (string | null)[]; nonEmpty: number }[] = []
  for (let r = 1; r <= rowCount; r += 1) {
    const row = ws.getRow(r)
    const texts: string[] = []
    const fills: string[] = []
    const fonts: string[] = []
    const notes: (string | null)[] = []
    let nonEmpty = 0
    for (let c = 1; c <= colCount; c += 1) {
      const cell = row.getCell(c)
      const t = cellText(cell)
      texts.push(t)
      fills.push(cellFill(cell))
      fonts.push(cellFont(cell))
      notes.push(cellNoteIndex(cell))
      if (t !== '') nonEmpty += 1
    }
    rows.push({ r, texts, fills, fonts, notes, nonEmpty })
  }


  // ---------- 自家导出（带 friday-meta）：精确解析，不走 monday 的启发式 ----------
  if (meta !== null && Array.isArray(meta.columns) && meta.columns.length > 0) {
    const allCols = meta.columns
    // meta 里可能带 name 列（自定义标题），它不参与取值，只用来建列
    const nameCol = allCols.find((c) => c.isName === true || c.type === 'name')
    const cols = allCols.filter((c) => !(c.isName === true || c.type === 'name'))
    const outGroups: { title: string; color: string }[] = []
    const outItems: { name: string; groupTitle: string; values: Record<string, string> }[] = []
    let currentGroup = ''

    // 导出文件里每个分组前都有一行表头（A 列 = Name），遇到就跳过；分组行是"只有一个非空单元格"
    for (let r = 1; r < rows.length; r += 1) {

      const row = rows[r]
      if (row === undefined) continue
      const first = (row.texts[0] ?? '').trim()
      if (row.nonEmpty === 0) continue
      // 分组行：只有一个非空单元格
      if (row.nonEmpty === 1 && first !== '') {
        currentGroup = first
        const font = row.fonts[0] ?? ''
        outGroups.push({ title: first, color: meaningful(font) ? nearestGroupColor(font) : 'blue' })
        continue
      }
      // 表头行（A 列是 Name）
      if (first === 'Name') continue
      if (first === '') continue
      const values: Record<string, string> = {}
      cols.forEach((c, i) => {
        // 导出时给颜色类单元格写了批注 friday:idx=N，优先用它（比按文字反查可靠）
        const noteIdx = row.notes[i + 1] ?? null
        if (noteIdx !== null && (c.type === 'color' || c.type === 'dropdown')) {
          values[c.key] = noteIdx
          return
        }
        const t = (row.texts[i + 1] ?? '').trim()
        if (t === '') return
        // 状态/下拉列的值必须是标签索引；导出写的是标签文字，这里反查回来
        if (c.type === 'color' || c.type === 'dropdown') {
          try {
            const rawLabels =
              typeof c.labels === 'string' ? c.labels : c.labels === undefined ? null : JSON.stringify(c.labels)
            const parsed = rawLabels === null ? null : (JSON.parse(rawLabels) as { labels?: Record<string, string> })
            const hit = Object.entries(parsed?.labels ?? {}).find(([, text]) => text === t)
            if (hit !== undefined) {
              values[c.key] = hit[0]
            } else if (/^\d+$/.test(t)) {
              // 导出没查到标签文字时写的是原始索引，直接用
              values[c.key] = t
            }
          } catch {
            if (/^\d+$/.test(t)) values[c.key] = t
          }
          return
        }
        values[c.key] = t
      })
      outItems.push({ name: first, groupTitle: currentGroup === '' ? '导入分组' : currentGroup, values })
    }
    if (outGroups.length === 0) outGroups.push({ title: '导入分组', color: 'blue' })
    return {
      payload: {
        version: 1,
        board: { name: rows[0]?.texts.find((t) => t !== '') ?? '导入的看板' },
        columns: [
          // 名称列（标题来自 meta，没有就沿用默认）
          { key: 'name', title: nameCol?.title ?? 'Item', type: 'name', width: nameCol?.width ?? 240 },
          ...cols.map((c) => ({
          key: c.key,
          title: c.title,
          type: c.type,
          width: c.width ?? Math.max(120, Math.min(240, c.title.length * 14)),
          labels: typeof c.labels === 'string' ? c.labels : c.labels === undefined ? undefined : JSON.stringify(c.labels),
          settings: typeof c.settings === 'string' ? c.settings : c.settings === undefined ? undefined : JSON.stringify(c.settings),
          })),
        ],
        groups: outGroups,
        items: outItems,
      },
      source: 'friday' as const,
      progressCandidates: [],
      warnings: [] as string[],
    }
  }

  const boardName = rows[0]?.texts.find((t) => t !== '') ?? 'monday 导入'
  let i = 1

  // 行 2 可能是描述（多格）也可能是分组名（单格）
  const groupRows: { title: string; color: string; rowIndex: number }[] = []
  let header: string[] | null = null

  const isGroupRow = (idx: number): boolean => (rows[idx]?.nonEmpty ?? 0) === 1 && (rows[idx + 1]?.nonEmpty ?? 0) >= 2

  while (i < rows.length) {
    const cur = rows[i]
    if (cur === undefined || cur.nonEmpty === 0) {
      i += 1
      continue
    }
    if (isGroupRow(i)) {
      const cellIdx = cur.texts.findIndex((t) => t !== '') ?? -1
      // 分组名的颜色在字体的 color 上；有的导出只有个别单元格带色，所以扫一遍取第一个有值的
      const font = cur.fonts.find((f) => meaningful(f)) ?? cur.fonts[cellIdx] ?? ''
      groupRows.push({
        title: cur.texts[cellIdx] ?? "",
        color: meaningful(font) ? nearestGroupColor(font) : 'blue',
        rowIndex: i,
      })
      i += 1
      continue
    }
    if (header === null && cur.nonEmpty >= 2) {
      header = cur.texts.map((t) => t)
      i += 1
      continue
    }
    i += 1
  }

  if (header === null) throw new Error('找不到表头行（需要一行有 ≥2 个非空单元格）')
  if (groupRows.length === 0) {
    // 没有显式分组行 → 建一个默认分组
    groupRows.push({ title: '导入分组', color: 'blue', rowIndex: -1 })
    warnings.push('文件里没有识别到分组行，已创建「导入分组」')
  }

  // 表头去掉末尾的 "Item ID (auto generated)" 之类列
  const skipCol = new Set<number>()
  header.forEach((h, idx) => {
    if (/^item id/i.test(h.trim())) skipCol.add(idx)
  })

  // 收集每个分组下的条目行
  const itemRows: { groupTitle: string; texts: string[]; fills: string[]; notes: (string | null)[] }[] = []
  const groupBounds = groupRows.map((g) => g.rowIndex).filter((x) => x >= 0).sort((a, b) => a - b)
  const headerSig = header.map((h) => h ?? '')
  const headerIndex = rows.findIndex((row) => row.nonEmpty >= 2 && headerSig.every((h, k) => h === (row.texts[k] ?? '')))
  void headerIndex
  for (let r = 0; r < rows.length; r += 1) {
    const row = rows[r]
    if (row === undefined || row.nonEmpty === 0) continue
    let gTitle = ''
    for (let gi = groupBounds.length - 1; gi >= 0; gi -= 1) {
      const bound = groupBounds[gi]
      if (bound !== undefined && r > bound) {
        const gr = groupRows.find((g) => g.rowIndex === bound)
        gTitle = gr?.title ?? ''
        break
      }
    }
    if (gTitle === '') continue
    // 表头行本身跳过
    if (headerSig.every((h, k) => h === (row.texts[k] ?? ''))) continue
    // 只有第一格有值、且下一行是表头 → 是另一个分组行，跳过
    if (row.nonEmpty === 1 && (rows[r + 1]?.nonEmpty ?? 0) >= 2) continue
    itemRows.push({ groupTitle: gTitle, texts: row.texts, fills: row.fills, notes: row.notes })
  }

  // ---- 列类型推断 ----
  const columns: MondayParseResult['payload']['columns'] = []
  const progressCandidates: { key: string; title: string; sample: string }[] = []
  header.forEach((rawTitle, idx) => {
    const title = rawTitle ?? ''
    // 第 0 列是条目名（Name），不单独建列
    if (idx === 0 || skipCol.has(idx) || title.trim() === '') return
    const key = `m_${idx + 1}`
    const values = itemRows.map((it) => it.texts[idx] ?? '').filter((v) => v !== '')
    const colorPairs = new Map<string, string>()
    for (const it of itemRows) {
      const t = it.texts[idx] ?? ''
      const fillHex = it.fills[idx] ?? ""
      const f = meaningful(fillHex) ? argb(fillHex) : ""
      if (t !== '' && f !== '') colorPairs.set(t, f)
    }

    let type = 'text'
    let labels: string | undefined
    let settings: string | undefined

    // 有些分组中间会再出现一次列名（重复表头），判定时要排除
    const realValues = values.filter((v) => v.trim() !== title.trim())

    // monday 的 Progress 列特征：值是 "34%" / "0%" 这种百分比（老版本是 0–100 的整数）。
    // 我们的 columns-battery 需要手工配数据源，导入还原不了，所以落成 numeric 并带百分比显示格式
    // （值存 0.34，numFormat.percent 显示成 34%）——比落成 color（几个孤立标签）更贴近原意。
    const pctAll = realValues.length >= 3 && realValues.every((v) => /^\d{1,3}%$/.test(v))
    const numericAll = realValues.length >= 3 && realValues.every((v) => /^\d{1,3}$/.test(v) && Number(v) <= 100)
    const looksLikeProgress = (pctAll || numericAll) && new Set(realValues).size >= 2

    const dateLikeRatio = values.length === 0 ? 0 : values.filter(isDateLike).length / values.length
    if (looksLikeProgress) {
      progressCandidates.push({ key, title, sample: realValues.slice(0, 3).join(' / ') })
      type = 'numeric'
      if (pctAll) settings = JSON.stringify({ numFormat: { percent: true, decimals: 0 } })
    } else if (dateLikeRatio >= 0.6) {
      type = 'date'
    } else if (colorPairs.size >= 2) {
      type = 'color'
      const labelsArr: Record<string, string> = {}
      const mapping: Record<string, string> = {}
      let n = 0
      for (const [text, hex] of colorPairs) {
        labelsArr[String(n)] = text
        mapping[String(n)] = `#${hex.toLowerCase()}`
        n += 1
      }
      labels = JSON.stringify({ labels: labelsArr })
      settings = JSON.stringify({ color_mapping: mapping })
    } else if (values.length > 0 && values.every(isNumberLike)) {
      type = 'numeric'
    } else if (values.length > 0 && values.every(isDateLike)) {
      type = 'date'
    } else if (values.length >= 2 && values.filter(isTagLike).length / values.length >= 0.6) {
      type = 'tag'
    }

    // 我们自己的导出：按 friday-meta 精确还原类型/宽度/标签/设置
    const metaCol = meta?.columns?.find((c) => c.title === title)
    if (metaCol !== undefined) {
      // meta 里的 labels/settings 可能已经是对象（API 数据原样），统一转回 JSON 字符串
      const asJson = (v: unknown): string | undefined =>
        v === undefined || v === null ? undefined : typeof v === 'string' ? v : JSON.stringify(v)
      columns.push({
        key,
        title,
        type: metaCol.type,
        width: metaCol.width ?? Math.max(120, Math.min(240, title.length * 14)),
        labels: asJson(metaCol.labels),
        settings: asJson(metaCol.settings),
      })
      return
    }

    columns.push({ key, title, type, width: Math.max(120, Math.min(240, title.length * 14)), labels, settings })
  })

  // ---- 条目 ----
  const items: MondayParseResult['payload']['items'] = []
  let footerRowsSkipped = 0
  for (const it of itemRows) {
    const name = (it.texts[0] ?? '').trim()
    // monday 每个分组末尾有一行「组尾汇总」（名称格为空、数值列有合计）—— 那是视图装饰，不是数据
    if (name === '') {
      footerRowsSkipped += 1
      continue
    }
    const values: Record<string, string> = {}
    let colIdx = 0
    header.forEach((title, idx) => {
      // 第 0 列是条目名，已经在上面用掉；这里不能再消费一个数据列
      if (idx === 0 || skipCol.has(idx) || title.trim() === '') return
      const col = columns[colIdx]
      colIdx += 1
      if (col === undefined) return
      const raw = (it.texts[idx] ?? '').trim()
      if (raw === '') return
      if (col.type === 'tag') {
        values[col.key] = JSON.stringify(raw.split(',').map((x) => x.trim()).filter((x) => x !== ''))
        } else if (col.type === 'numeric') {
          const isPct = /%$/.test(raw)
          const num = raw.replace(/[%,]/g, '')
          values[col.key] = isPct ? String(Number(num) / 100) : num
      } else if (col.type === 'color') {
        // 值必须是标签索引：优先用我们导出时留下的批注（friday:idx=N），否则按文字反查
        const labelsJson = JSON.parse(col.labels ?? '{"labels":{}}') as { labels: Record<string, string> }
        const hinted = it.notes[idx] ?? null
        if (hinted !== null && labelsJson.labels[hinted] !== undefined) {
          values[col.key] = hinted
        } else {
          const found = Object.entries(labelsJson.labels).find(([, text]) => text === raw)
          values[col.key] = found === undefined ? '' : found[0]
          if (values[col.key] === '') delete values[col.key]
        }
      } else {
        values[col.key] = raw
      }
    })
    if (name === '' && Object.keys(values).length === 0) continue
    items.push({ name: name === '' ? '未命名' : name, groupTitle: it.groupTitle, values })
  }

  return {
    payload: {
      version: 1,
      board: { name: boardName },
      columns,
      groups: groupRows.map((g) => ({ title: g.title, color: g.color })),
      items,
    },
    source: meta !== null ? 'friday' : 'monday',
    progressCandidates,
    warnings:
      footerRowsSkipped > 0
        ? [...warnings, `已跳过 ${footerRowsSkipped} 行 monday 组尾汇总（不是数据）`]
        : warnings,
  }
}
